// data/items/setArmor.js
// Set armor dropped by Ashen Crucible Tier 2 bosses
// 3 sets × 6 pieces × 2 states (dormant/awakened) = 36 items
//
// Dormant: equippable with good stats (above T6 crafted), no set bonus eligibility
// Awakened: same stats, now counts toward 2/4/6pc set thresholds
// Awakening costs 130 Veil Embers per piece at the Awakening Store
//
// Inverse triangle — each boss drops gear for a DIFFERENT combat style:
//   Searing Matriarch (weak to melee)  → drops Pyreweave (magic)
//   Molten Colossus   (weak to magic)  → drops Cindermark (ranged)
//   Cindered Knight   (weak to ranged) → drops Ashforged (melee)

// ============================================================
// SET CONFIGURATION
// ============================================================

const SET_CONFIGS = [
    {
        setId: 'ashforged',
        setName: 'Ashforged',
        armorType: 'melee',
        color: '#c0392b',
        dropSource: 'cindered_knight',
        combatLevel: 80,
        // Style-specific stat: attackBonus (melee damage)
        styleStatKey: 'attackBonus',
        // Small accuracy bonus per piece (melee armor pattern)
        accuracyKey: 'meleeAccuracy',
        accuracyRatio: 0.3,  // 30% of styleStat value
        // Defense multiplier: melee gets highest defense (T7 = ~400 total)
        defenseMultiplier: 1.0,
        pieces: [
            { slot: 'head', name: 'Helm' },
            { slot: 'body', name: 'Chestplate' },
            { slot: 'legs', name: 'Platelegs' },
            { slot: 'feet', name: 'Sabatons' },
            { slot: 'hands', name: 'Gauntlets' },
            { slot: 'shoulders', name: 'Pauldrons' }
        ]
    },
    {
        setId: 'cindermark',
        setName: 'Cindermark',
        armorType: 'ranged',
        color: '#d35400',
        dropSource: 'molten_colossus',
        combatLevel: 80,
        // Style-specific stat: attackBonus (ranged damage)
        styleStatKey: 'attackBonus',
        accuracyKey: 'rangedAccuracy',
        accuracyRatio: 0.3,
        // Defense multiplier: ranged gets medium defense (~85% of melee)
        defenseMultiplier: 0.85,
        pieces: [
            { slot: 'head', name: 'Helm' },
            { slot: 'body', name: 'Chestplate' },
            { slot: 'legs', name: 'Legguards' },
            { slot: 'feet', name: 'Boots' },
            { slot: 'hands', name: 'Gauntlets' },
            { slot: 'shoulders', name: 'Vambraces' }
        ]
    },
    {
        setId: 'pyreweave',
        setName: 'Pyreweave',
        armorType: 'magic',
        color: '#8e44ad',
        dropSource: 'searing_matriarch',
        combatLevel: 80,
        // Style-specific stat: spellDamage (percentage, values divided by 100)
        styleStatKey: 'spellDamage',
        styleStatDivisor: 100,  // Convert integer budget to decimal (5 → 0.05)
        // Magic gets 30% higher styleStat to compensate for lowest defense
        styleStatMultiplier: 1.30,
        accuracyKey: 'magicAccuracy',
        accuracyRatio: 1.5,  // Higher ratio — magic armor gives more accuracy
        // Defense multiplier: magic gets lowest defense (~50% of melee) - trade for higher damage
        defenseMultiplier: 0.50,
        pieces: [
            { slot: 'head', name: 'Hood' },
            { slot: 'body', name: 'Robe' },
            { slot: 'legs', name: 'Leggings' },
            { slot: 'feet', name: 'Slippers' },
            { slot: 'hands', name: 'Wraps' },
            { slot: 'shoulders', name: 'Mantle' }
        ]
    }
];

// ============================================================
// STAT BUDGETS PER SLOT
// Target: Tier 7 defense (above T6 crafted), with style-appropriate
// defense scaling via defenseMultiplier in SET_CONFIGS.
// T6 melee base = 54, T7 base = 70 (following +16 progression)
// Full melee set: 70 * 5.7 (slot modifiers) = 399 DEF
// Real power comes from 2/4/6pc set bonuses (Phase 6).
// ============================================================

// T7 base defense values following armor.js slot modifier pattern
// (0.8 + 0.9 + 0.6 + 1.5 + 1.2 + 0.7 = 5.7 total multiplier)
// Style stat increased to boost melee DPS closer to ~90 (was ~73)
const SLOT_BUDGETS = {
    head:      { defenseBonus: 56, styleStat: 8, speedBonus: 2, value: 2000 },   // 70 * 0.8
    body:      { defenseBonus: 105, styleStat: 15, value: 3500 },                // 70 * 1.5
    legs:      { defenseBonus: 84, styleStat: 10, speedBonus: 3, value: 2800 },  // 70 * 1.2
    feet:      { defenseBonus: 49, styleStat: 7, speedBonus: 2, value: 1800 },   // 70 * 0.7
    hands:     { defenseBonus: 42, styleStat: 6, critChance: 2, value: 1500 },   // 70 * 0.6
    shoulders: { defenseBonus: 63, styleStat: 9, value: 2200 }                   // 70 * 0.9
};
// Total melee defense: 56+105+84+49+42+63 = 399 (proper T7)
// Total ranged defense: 399 * 0.85 = ~339 (medium defense)
// Total magic defense: 399 * 0.50 = ~200 (lowest, but highest DPS)
// Total style stat: 8+15+10+7+6+9 = 55 (attackBonus for melee/ranged, 0.55 spellDamage for magic)

// ============================================================
// GENERATION
// ============================================================

function generateSetArmor() {
    const dormant = [];
    const awakened = [];

    for (const config of SET_CONFIGS) {
        for (const piece of config.pieces) {
            const budget = SLOT_BUDGETS[piece.slot];
            const slotId = piece.slot === 'body' ? 'chestplate'
                         : piece.slot === 'legs' ? 'legguards'
                         : piece.slot === 'feet' ? 'boots'
                         : piece.slot === 'hands' ? 'gloves'
                         : piece.slot;  // head, shoulders stay as-is

            // Apply defense multiplier based on armor type
            // Melee: 1.0 (full ~399), Ranged: 0.85 (~339), Magic: 0.50 (~200)
            const scaledDefense = Math.round(budget.defenseBonus * (config.defenseMultiplier || 1.0));

            // Base item properties shared by dormant and awakened
            const baseProps = {
                color: config.color,
                tier: 7,  // Above T6 crafted
                armorType: config.armorType,
                slot: piece.slot,
                craftSlot: slotId,
                defenseBonus: scaledDefense,
                combatLevel: config.combatLevel,
                value: budget.value,
                setId: config.setId,
                dropSource: config.dropSource
            };

            // Add style-specific stat (with optional multiplier for magic compensation)
            const styleStatMultiplier = config.styleStatMultiplier || 1.0;
            const scaledStyleStat = budget.styleStat * styleStatMultiplier;
            const styleValue = config.styleStatDivisor
                ? parseFloat((scaledStyleStat / config.styleStatDivisor).toFixed(3))
                : Math.round(scaledStyleStat);
            baseProps[config.styleStatKey] = styleValue;

            // Add accuracy bonus
            const accuracyValue = Math.round(budget.styleStat * config.accuracyRatio);
            if (accuracyValue > 0) {
                baseProps[config.accuracyKey] = accuracyValue;
            }

            // Add secondary stats (speed, crit)
            if (budget.speedBonus) baseProps.speedBonus = budget.speedBonus;
            if (budget.critChance) baseProps.critChance = budget.critChance;

            // Dormant variant — equippable, no set bonus eligibility
            const dormantId = `dormant_${config.setId}_${slotId}`;
            dormant.push({
                id: dormantId,
                name: `Dormant ${config.setName} ${piece.name}`,
                ...baseProps,
                awakened: false
            });

            // Awakened variant — same stats, counts toward set bonuses
            const awakenedId = `awakened_${config.setId}_${slotId}`;
            awakened.push({
                id: awakenedId,
                name: `Awakened ${config.setName} ${piece.name}`,
                ...baseProps,
                awakened: true
            });
        }
    }

    return { dormant, awakened };
}

const { dormant, awakened } = generateSetArmor();

export const DORMANT_SET_ARMOR = dormant;
export const AWAKENED_SET_ARMOR = awakened;
export const ALL_SET_ARMOR = [...dormant, ...awakened];

// Helper: get all pieces for a specific set
export function getSetPieces(setId, awakenedOnly = false) {
    return ALL_SET_ARMOR.filter(item =>
        item.setId === setId && (!awakenedOnly || item.awakened)
    );
}

// Helper: get the awakened version ID for a dormant piece
export function getAwakenedId(dormantItemId) {
    return dormantItemId.replace('dormant_', 'awakened_');
}

// Helper: get the dormant version ID for an awakened piece
export function getDormantId(awakenedItemId) {
    return awakenedItemId.replace('awakened_', 'dormant_');
}

// Set metadata for UI display
export const SET_INFO = {
    ashforged: {
        name: 'Ashforged',
        armorType: 'melee',
        color: '#c0392b',
        description: 'Heavy armor forged in the crucible. Sustained brawler set — builds Momentum through consecutive hits.',
        dropSource: 'cindered_knight'
    },
    cindermark: {
        name: 'Cindermark',
        armorType: 'ranged',
        color: '#d35400',
        description: 'Cinder-touched leather. Bleed burst set — accelerates bleeds and fires piercing shots.',
        dropSource: 'molten_colossus'
    },
    pyreweave: {
        name: 'Pyreweave',
        armorType: 'magic',
        color: '#8e44ad',
        description: 'Cloth woven from pyre flames. Spell cascade set — echoes and chains spell effects.',
        dropSource: 'searing_matriarch'
    }
};

export default ALL_SET_ARMOR;
